/**
 * New Evaluation page — the core user flow.
 *
 * Steps:
 * 1. Select company from dropdown (loads principles)
 * 2. Select role (MLE, PM, TPM, EM)
 * 3. Select experience level
 * 4. Pick a question (from bank or type custom)
 * 5. Paste STAR-formatted answer
 * 6. Submit → navigate to EvaluationDetail
 *
 * Uses plain useState for form state, validation on submit.
 */

import React, { useCallback, useRef, useState } from "react";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Trans, useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import AssignmentIcon from "@mui/icons-material/Assignment";
import LinearProgress from "@mui/material/LinearProgress";

import {
  listCompanies,
  listQuestions,
  listTemplates,
  getTemplate,
  getRandomQuestion,
  createAnswer,
  createEvaluation,
  importAnswers,
  listScenarios,
  getScenarioCategories,
  Company,
  Question,
  Scenario,
  AnswerTemplate,
  ImportResponse,
} from "../api/client";
import UpgradePrompt, { isTierLimitError } from "../components/UpgradePrompt";
import VoiceInput from "../components/VoiceInput";
import { toUserSafeErrorMessage } from "../utils/errorMessage";

const ROLES = ["MLE", "PM", "TPM", "EM"];
const LEVELS = [
  { value: "entry", labelKey: "NewEvaluation.levelEntryLabel" },
  { value: "mid", labelKey: "NewEvaluation.levelMidLabel" },
  { value: "senior", labelKey: "NewEvaluation.levelSeniorLabel" },
  { value: "staff", labelKey: "NewEvaluation.levelStaffLabel" },
  { value: "manager", labelKey: "NewEvaluation.levelManagerLabel" },
];

export default function NewEvaluation() {
  const { t } = useTranslation("pages-products");
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const passedQuestionId = (location.state as any)?.questionId || "";

  // Form state
  const [track, setTrack] = useState("standard");
  const [companyId, setCompanyId] = useState("");
  const [role, setRole] = useState("");
  const [level, setLevel] = useState("");
  const [agenticCategory, setAgenticCategory] = useState("");
  const [agenticType, setAgenticType] = useState("behavioral");
  const [selectedScenarioId, setSelectedScenarioId] = useState("");

  const isAgentic = track === "agentic";
  const [questionMode, setQuestionMode] = useState<"bank" | "custom">("bank");
  const [selectedQuestionId, setSelectedQuestionId] = useState(passedQuestionId);
  const [customQuestion, setCustomQuestion] = useState("");
  const [answerText, setAnswerText] = useState("");
  const [error, setError] = useState("");
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [tierLimitInfo, setTierLimitInfo] = useState<{
    currentUsage?: number;
    limit?: number;
  }>({});

  // Import state
  const [importResult, setImportResult] = useState<ImportResponse | null>(null);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Data queries
  const {
    data: companies = [],
    isLoading: loadingCompanies,
    isError: companiesError,
  } = useQuery({
    queryKey: ["companies"],
    queryFn: listCompanies,
  });

  // Templates query — only fetch if user might be authenticated
  const { data: templates = [] } = useQuery({
    queryKey: ["templates"],
    queryFn: listTemplates,
    // Silently fail for unauthenticated users
    retry: false,
  });

  const {
    data: questionList,
    isLoading: loadingQuestions,
    isError: questionsError,
  } = useQuery({
    queryKey: ["questions", role, level],
    queryFn: () =>
      listQuestions({
        role: role || undefined,
        level: level || undefined,
        limit: 100,
      }),
    enabled: questionMode === "bank" && !isAgentic,
  });

  // Agentic scenario queries
  const { data: scenarios } = useQuery({
    queryKey: ["scenarios", agenticCategory, agenticType],
    queryFn: () =>
      listScenarios({
        track: "agentic",
        category: agenticCategory || undefined,
        interview_type: agenticType || undefined,
      }),
    enabled: isAgentic,
  });

  const { data: categories = [] } = useQuery({
    queryKey: ["scenario-categories", agenticType],
    queryFn: () => getScenarioCategories("agentic", agenticType),
    enabled: isAgentic,
  });

  // Random question
  const randomMutation = useMutation({
    mutationFn: () =>
      getRandomQuestion({
        role: role || undefined,
        level: level || undefined,
      }),
    onSuccess: (q: Question) => {
      setSelectedQuestionId(q.id);
    },
  });

  // Submit mutation
  const submitMutation = useMutation({
    mutationFn: async () => {
      // Determine question text for agentic scenarios
      let questionText: string | undefined;
      if (isAgentic && selectedScenarioId) {
        const scenario = scenarios?.items.find(
          (s) => s.id === selectedScenarioId
        );
        questionText = scenario?.question;
      }

      // Create answer — agentic track doesn't require company
      const answer = await createAnswer({
        question_id:
          !isAgentic && questionMode === "bank"
            ? selectedQuestionId
            : undefined,
        custom_question_text: isAgentic
          ? questionText || customQuestion || undefined
          : questionMode === "custom"
          ? customQuestion
          : undefined,
        target_company_id: isAgentic ? undefined : companyId,
        target_role: isAgentic ? "AGENTIC" : role,
        experience_level: isAgentic ? "senior" : level,
        answer_text: answerText,
        track: track,
        interview_type: isAgentic ? agenticType : "behavioral",
      });

      // Get the first version's ID
      // The answer response doesn't include versions, so we need to
      // create the evaluation using the answer detail endpoint
      const answerDetail = await import("../api/client").then((m) =>
        m.getAnswer(answer.id)
      );
      if (!answerDetail.versions?.length) {
        throw new Error(t("NewEvaluation.noVersionReturnedError"));
      }
      const versionId = answerDetail.versions[0].id;

      // Create evaluation
      const evaluation = await createEvaluation(versionId);
      return evaluation;
    },
    onSuccess: (evaluation) => {
      navigate(`/evaluations/${evaluation.id}`);
    },
    onError: (err: any) => {
      const tierCheck = isTierLimitError(err);
      if (tierCheck.isLimit) {
        setTierLimitInfo({
          currentUsage: tierCheck.currentUsage,
          limit: tierCheck.limit,
        });
        setShowUpgrade(true);
        return;
      }
      setError(toUserSafeErrorMessage(err, t("NewEvaluation.submitFailedError")));
    },
  });

  const handleSubmit = () => {
    setError("");

    if (isAgentic) {
      // Agentic validation — simpler, no company required
      if (!selectedScenarioId && !customQuestion.trim())
        return setError(t("NewEvaluation.selectScenarioError"));
      if (answerText.trim().split(/\s+/).length < 10)
        return setError(t("NewEvaluation.answerTooShortError"));
    } else {
      // Standard validation
      if (!companyId) return setError(t("NewEvaluation.selectCompanyError"));
      if (!role) return setError(t("NewEvaluation.selectRoleError"));
      if (!level) return setError(t("NewEvaluation.selectLevelError"));
      if (questionMode === "bank" && !selectedQuestionId)
        return setError(t("NewEvaluation.selectQuestionError"));
      if (questionMode === "custom" && !customQuestion.trim())
        return setError(t("NewEvaluation.enterCustomQuestionError"));
      if (answerText.trim().split(/\s+/).length < 10)
        return setError(t("NewEvaluation.answerTooShortError"));
    }

    submitMutation.mutate();
  };

  // Import handlers
  const handleImportFile = useCallback(
    async (file: File) => {
      // Validate context first
      if (!companyId || !role || !level) {
        setImportError(t("NewEvaluation.importContextError"));
        return;
      }

      const validExts = [".txt", ".md"];
      const ext = file.name.toLowerCase().slice(file.name.lastIndexOf("."));
      if (!validExts.includes(ext)) {
        setImportError(t("NewEvaluation.importFileTypeError"));
        return;
      }

      if (file.size > 100 * 1024) {
        setImportError(t("NewEvaluation.importFileSizeError"));
        return;
      }

      setImporting(true);
      setImportError(null);
      setImportResult(null);

      try {
        const result = await importAnswers(file, companyId, role, level);
        setImportResult(result);
      } catch (err: any) {
        setImportError(toUserSafeErrorMessage(err, t("NewEvaluation.importFailedError")));
      } finally {
        setImporting(false);
      }
    },
    [companyId, role, level, t]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) handleImportFile(file);
    },
    [handleImportFile]
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleImportFile(file);
      // Reset input so same file can be re-selected
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    [handleImportFile]
  );

  const wordCount = answerText.trim()
    ? answerText.trim().split(/\s+/).length
    : 0;

  const selectedQuestion = questionList?.items.find(
    (q) => q.id === selectedQuestionId
  );

  // Progress calculation — rough step completion tracker
  const progressPct = (() => {
    let filled = 0;
    const total = 3;
    if (isAgentic) {
      if (agenticType) filled += 1;
      if (selectedScenarioId || customQuestion.trim()) filled += 1;
      if (answerText.trim().split(/\s+/).length >= 10) filled += 1;
    } else {
      if (companyId && role && level) filled += 1;
      if (
        (questionMode === "bank" && selectedQuestionId) ||
        (questionMode === "custom" && customQuestion.trim())
      )
        filled += 1;
      if (answerText.trim().split(/\s+/).length >= 10) filled += 1;
    }
    return (filled / total) * 100;
  })();

  return (
    <Box sx={{ maxWidth: 800, mx: "auto" }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{ fontSize: { xs: "1.5rem", sm: "2.125rem" } }}
      >
        {t("NewEvaluation.pageTitle")}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        {isAgentic
          ? t("NewEvaluation.pageIntroAgentic")
          : t("NewEvaluation.pageIntroStandard")}
      </Typography>

      {/* Track Selector — pill-style toggle */}
      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
        <Card
          variant={track === "standard" ? "elevation" : "outlined"}
          sx={{
            flex: 1,
            cursor: "pointer",
            p: 0,
            border: 2,
            borderColor: track === "standard" ? "primary.main" : "divider",
            transition: "all 0.15s ease",
            "&:hover": {
              borderColor: track === "standard" ? "primary.main" : "primary.light",
            },
          }}
          onClick={() => setTrack("standard")}
        >
          <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <AssignmentIcon
                sx={{ color: track === "standard" ? "primary.main" : "text.secondary", fontSize: 22 }}
              />
              <Box>
                <Typography variant="subtitle2" fontWeight={500}>
                  {t("NewEvaluation.standardTrackTitle")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("NewEvaluation.standardTrackSubtitle")}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
        <Card
          variant={track === "agentic" ? "elevation" : "outlined"}
          sx={{
            flex: 1,
            cursor: "pointer",
            p: 0,
            border: 2,
            borderColor: track === "agentic" ? "secondary.main" : "divider",
            transition: "all 0.15s ease",
            "&:hover": {
              borderColor: track === "agentic" ? "secondary.main" : "secondary.light",
            },
          }}
          onClick={() => setTrack("agentic")}
        >
          <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <SmartToyIcon
                sx={{ color: track === "agentic" ? "secondary.main" : "text.secondary", fontSize: 22 }}
              />
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <Typography variant="subtitle2" fontWeight={500}>
                    {t("NewEvaluation.agenticTrackTitle")}
                  </Typography>
                  <Chip
                    label={t("NewEvaluation.newChipLabel")}
                    size="small"
                    color="success"
                    sx={{ height: 18, fontSize: 10, fontWeight: 500 }}
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {t("NewEvaluation.agenticTrackSubtitle")}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      {/* Progress indicator */}
      <Box sx={{ mb: 3 }}>
        <LinearProgress
          variant="determinate"
          value={progressPct}
          sx={{
            height: 4,
            borderRadius: 2,
            bgcolor: "grey.100",
            "& .MuiLinearProgress-bar": {
              borderRadius: 2,
              transition: "transform 0.4s ease",
              bgcolor: progressPct === 100 ? "secondary.main" : "primary.main",
            },
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
          {progressPct === 0
            ? t("NewEvaluation.progressEmptyLabel")
            : progressPct === 100
            ? t("NewEvaluation.progressReadyLabel")
            : t("NewEvaluation.progressPercentLabel", { percent: Math.round(progressPct) })}
        </Typography>
      </Box>

      <Stack spacing={3}>
        {/* Context Selection — different for each track */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {isAgentic ? t("NewEvaluation.step1AgenticTitle") : t("NewEvaluation.step1StandardTitle")}
            </Typography>
            <Stack spacing={2}>
              {isAgentic ? (
                /* Agentic track — type + category selectors */
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <FormControl fullWidth>
                    <InputLabel>{t("NewEvaluation.typeLabel")}</InputLabel>
                    <Select
                      value={agenticType}
                      label={t("NewEvaluation.typeLabel")}
                      onChange={(e) => {
                        setAgenticType(e.target.value);
                        setAgenticCategory("");
                        setSelectedScenarioId("");
                      }}
                    >
                      <MenuItem value="behavioral">{t("NewEvaluation.behavioralOption")}</MenuItem>
                      <MenuItem value="system_design">{t("NewEvaluation.systemDesignOption")}</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel>{t("NewEvaluation.categoryOptionalLabel")}</InputLabel>
                    <Select
                      value={agenticCategory}
                      label={t("NewEvaluation.categoryOptionalLabel")}
                      onChange={(e) => setAgenticCategory(e.target.value)}
                    >
                      <MenuItem value="">{t("NewEvaluation.allCategoriesOption")}</MenuItem>
                      {categories.map((c: string) => (
                        <MenuItem key={c} value={c}>
                          {c.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              ) : (
                /* Standard track — company + role + level */
                <>
              <FormControl fullWidth error={companiesError}>
                <InputLabel>{t("NewEvaluation.companyLabel")}</InputLabel>
                <Select
                  value={companyId}
                  label={t("NewEvaluation.companyLabel")}
                  onChange={(e) => setCompanyId(e.target.value)}
                  disabled={loadingCompanies}
                >
                  {companies.map((c: Company) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}{" "}
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 1 }}
                      >
                        ({c.principle_type})
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
                {companiesError && (
                  <FormHelperText error>
                    {t("NewEvaluation.companiesLoadError")}
                  </FormHelperText>
                )}
                {!loadingCompanies && !companiesError && !companies.length && (
                  <FormHelperText>{t("NewEvaluation.noCompaniesMessage")}</FormHelperText>
                )}
              </FormControl>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>{t("NewEvaluation.roleLabel")}</InputLabel>
                  <Select
                    value={role}
                    label={t("NewEvaluation.roleLabel")}
                    onChange={(e) => setRole(e.target.value)}
                  >
                    {ROLES.map((r) => (
                      <MenuItem key={r} value={r}>
                        {r}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>{t("NewEvaluation.experienceLevelLabel")}</InputLabel>
                  <Select
                    value={level}
                    label={t("NewEvaluation.experienceLevelLabel")}
                    onChange={(e) => setLevel(e.target.value)}
                  >
                    {LEVELS.map((l) => (
                      <MenuItem key={l.value} value={l.value}>
                        {t(l.labelKey)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
                </>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Question Selection */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {isAgentic ? t("NewEvaluation.step2AgenticTitle") : t("NewEvaluation.step2StandardTitle")}
            </Typography>

            {isAgentic ? (
              /* Agentic scenario selector */
              <Stack spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>{t("NewEvaluation.selectScenarioLabel")}</InputLabel>
                  <Select
                    value={selectedScenarioId}
                    label={t("NewEvaluation.selectScenarioLabel")}
                    onChange={(e) => setSelectedScenarioId(e.target.value)}
                  >
                    {scenarios?.items.map((s: Scenario) => (
                      <MenuItem key={s.id} value={s.id}>
                        <Box>
                          <Typography variant="body2" sx={{ maxWidth: 600, whiteSpace: "normal" }}>
                            {s.question}
                          </Typography>
                          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                            <Chip
                              label={s.category.replace(/_/g, " ")}
                              size="small"
                              variant="outlined"
                            />
                            <Chip
                              label={s.difficulty}
                              size="small"
                              color={s.difficulty === "expert" ? "error" : "warning"}
                            />
                            <Chip
                              label={s.type === "system_design" ? t("NewEvaluation.systemDesignOption") : t("NewEvaluation.behavioralOption")}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </Stack>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Empty state: no scenarios match the current filters */}
                {scenarios && scenarios.items.length === 0 && (
                  <Alert severity="info">
                    {t("NewEvaluation.noScenariosMessage")}
                  </Alert>
                )}

                {selectedScenarioId && scenarios?.items.find(s => s.id === selectedScenarioId) && (
                  <Alert severity="info">
                    <Trans
                      i18nKey="NewEvaluation.selectedScenarioLabel"
                      t={t}
                      values={{ question: scenarios.items.find(s => s.id === selectedScenarioId)!.question }}
                      components={[<strong key="strong0" />]}
                    />
                  </Alert>
                )}

                <Typography variant="body2" color="text.secondary">
                  {t("NewEvaluation.orCustomAgenticQuestionText")}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label={t("NewEvaluation.customQuestionOptionalLabel")}
                  value={customQuestion}
                  onChange={(e) => {
                    setCustomQuestion(e.target.value);
                    if (e.target.value) setSelectedScenarioId("");
                  }}
                  placeholder={t("NewEvaluation.customAgenticQuestionPlaceholder")}
                />
              </Stack>
            ) : (
            <>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Chip
                label={t("NewEvaluation.fromQuestionBankChipLabel")}
                variant={questionMode === "bank" ? "filled" : "outlined"}
                color={questionMode === "bank" ? "primary" : "default"}
                onClick={() => setQuestionMode("bank")}
              />
              <Chip
                label={t("NewEvaluation.customQuestionChipLabel")}
                variant={questionMode === "custom" ? "filled" : "outlined"}
                color={questionMode === "custom" ? "primary" : "default"}
                onClick={() => setQuestionMode("custom")}
              />
            </Stack>

            {questionMode === "bank" ? (
              <Stack spacing={2}>
                <FormControl fullWidth error={questionsError}>
                  <InputLabel>{t("NewEvaluation.selectQuestionLabel")}</InputLabel>
                  <Select
                    value={selectedQuestionId}
                    label={t("NewEvaluation.selectQuestionLabel")}
                    onChange={(e) => setSelectedQuestionId(e.target.value)}
                    disabled={loadingQuestions}
                  >
                    {questionList?.items.map((q: Question) => (
                      <MenuItem key={q.id} value={q.id}>
                        <Box>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 600 }}>
                            {q.question_text}
                          </Typography>
                          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                            {q.competency_tags.slice(0, 2).map((tag) => (
                              <Chip key={tag} label={tag} size="small" variant="outlined" />
                            ))}
                            <Chip
                              label={q.difficulty}
                              size="small"
                              color={
                                q.difficulty === "advanced"
                                  ? "warning"
                                  : q.difficulty === "senior_plus"
                                  ? "error"
                                  : "default"
                              }
                            />
                          </Stack>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                  {questionsError && (
                    <FormHelperText error>
                      {t("NewEvaluation.questionsLoadError")}
                    </FormHelperText>
                  )}
                  {!loadingQuestions &&
                    !questionsError &&
                    !questionList?.items?.length && (
                      <FormHelperText>
                        {t("NewEvaluation.noQuestionsMessage")}
                      </FormHelperText>
                    )}
                </FormControl>

                <Button
                  variant="outlined"
                  startIcon={<ShuffleIcon />}
                  onClick={() => randomMutation.mutate()}
                  disabled={randomMutation.isPending}
                  size="small"
                >
                  {t("NewEvaluation.surpriseMeButton")}
                </Button>

                {selectedQuestion && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <Trans
                      i18nKey="NewEvaluation.selectedQuestionLabel"
                      t={t}
                      values={{ question: selectedQuestion.question_text }}
                      components={[<strong key="strong0" />]}
                    />
                  </Alert>
                )}
              </Stack>
            ) : (
              <TextField
                fullWidth
                multiline
                minRows={2}
                label={t("NewEvaluation.typeBehavioralQuestionLabel")}
                value={customQuestion}
                onChange={(e) => setCustomQuestion(e.target.value)}
                placeholder={t("NewEvaluation.behavioralQuestionPlaceholder")}
              />
            )}
            </>
            )}
          </CardContent>
        </Card>

        {/* Answer Input */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {isAgentic ? t("NewEvaluation.step3AgenticTitle") : t("NewEvaluation.step3StandardTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {isAgentic
                ? t("NewEvaluation.answerInstructionsAgentic")
                : t("NewEvaluation.answerInstructionsStandard")}
            </Typography>

            {/* Template selector */}
            {templates.length > 0 && (
              <FormControl fullWidth sx={{ mb: 2 }} size="small">
                <InputLabel>{t("NewEvaluation.loadFromTemplateLabel")}</InputLabel>
                <Select
                  value=""
                  label={t("NewEvaluation.loadFromTemplateLabel")}
                  onChange={async (e) => {
                    const templateId = e.target.value as string;
                    if (!templateId) return;
                    try {
                      const tmpl = await getTemplate(templateId);
                      setAnswerText(tmpl.template_text);
                    } catch {
                      // Silently fail — user can still type manually
                    }
                  }}
                >
                  {templates.map((template: AnswerTemplate) => (
                    <MenuItem key={template.id} value={template.id}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2">{template.name}</Typography>
                        {template.is_default && (
                          <Chip label={t("NewEvaluation.defaultChipLabel")} size="small" color="primary" variant="outlined" />
                        )}
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              fullWidth
              multiline
              minRows={8}
              maxRows={20}
              value={answerText}
              onChange={(e) => setAnswerText(e.target.value)}
              placeholder={
                isAgentic
                  ? t("NewEvaluation.answerPlaceholderAgentic")
                  : t("NewEvaluation.answerPlaceholderStandard")
              }
            />
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
              <VoiceInput
                onTranscript={(text) =>
                  setAnswerText((prev) =>
                    prev ? prev.trimEnd() + " " + text : text
                  )
                }
                disabled={submitMutation.isPending}
              />
              <Typography
                variant="caption"
                color={
                  wordCount < 50
                    ? "error"
                    : wordCount > 500
                    ? "warning.main"
                    : "text.secondary"
                }
              >
                {t("NewEvaluation.wordCountLabel", { count: wordCount })}
                {wordCount < 50 && t("NewEvaluation.wordCountTooShortHint")}
                {wordCount > 500 && t("NewEvaluation.wordCountTooLongHint")}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Bulk Import */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t("NewEvaluation.importSectionTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("NewEvaluation.importSectionDescription")}
            </Typography>

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".txt,.md"
              style={{ display: "none" }}
              onChange={handleFileSelect}
            />

            {/* Drop zone */}
            <Box
              role="button"
              tabIndex={0}
              aria-label={t("NewEvaluation.dropZoneAriaLabel")}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
              sx={{
                border: 2,
                borderStyle: "dashed",
                borderColor: dragOver ? "primary.main" : "divider",
                borderRadius: 2,
                p: 3,
                textAlign: "center",
                cursor: "pointer",
                bgcolor: dragOver ? "primary.50" : "transparent",
                transition: "all 0.2s",
                "&:hover": {
                  borderColor: "primary.light",
                  bgcolor: "action.hover",
                },
                "&:focus-visible": {
                  outline: 2,
                  outlineColor: "primary.main",
                  outlineOffset: 2,
                },
              }}
            >
              {importing ? (
                <CircularProgress size={32} />
              ) : (
                <>
                  <UploadFileIcon
                    sx={{ fontSize: 40, color: "text.secondary", mb: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {t("NewEvaluation.dropZoneText")}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    {t("NewEvaluation.dropZoneFileHint")}
                  </Typography>
                </>
              )}
            </Box>

            {/* Import error */}
            {importError && (
              <Alert severity="error" sx={{ mt: 2 }} onClose={() => setImportError(null)}>
                {importError}
              </Alert>
            )}

            {/* Import result */}
            <Collapse in={!!importResult}>
              {importResult && (
                <Alert
                  severity="success"
                  icon={<CheckCircleIcon />}
                  sx={{ mt: 2 }}
                >
                  <Typography variant="subtitle2">
                    {t("NewEvaluation.importedCountLabel", {
                      count: importResult.total_found,
                      imported: importResult.imported_count,
                    })}
                  </Typography>
                  {importResult.answers.map((a) => (
                    <Typography key={a.answer_id} variant="body2">
                      • {a.question_text || t("NewEvaluation.customAnswerFallbackLabel")}{" "}
                      {t("NewEvaluation.importedAnswerWordCount", { words: a.word_count })}
                    </Typography>
                  ))}
                  {importResult.errors.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="warning.main">
                        {t("NewEvaluation.importWarningsLabel", { warnings: importResult.errors.join("; ") })}
                      </Typography>
                    </Box>
                  )}
                </Alert>
              )}
            </Collapse>
          </CardContent>
        </Card>

        {/* Error Display */}
        {error && (
          <Alert severity="error" onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {/* Submit */}
        <Button
          variant="contained"
          size="large"
          endIcon={
            submitMutation.isPending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <SendIcon />
            )
          }
          onClick={handleSubmit}
          disabled={submitMutation.isPending}
          sx={{
            py: 1.5,
            px: 4,
            fontSize: "1rem",
            background: isAgentic
              ? `linear-gradient(135deg, ${theme.palette.success.dark} 0%, ${theme.palette.success.main} 100%)`
              : `linear-gradient(135deg, ${theme.palette.text.primary} 0%, ${theme.palette.info.main} 100%)`,
            "&:hover": {
              background: isAgentic
                ? `linear-gradient(135deg, ${theme.palette.success.dark} 0%, ${theme.palette.success.dark} 100%)`
                : `linear-gradient(135deg, ${theme.palette.text.primary} 0%, ${theme.palette.text.primary} 100%)`,
            },
          }}
        >
          {submitMutation.isPending
            ? t("NewEvaluation.evaluatingButton")
            : isAgentic
            ? t("NewEvaluation.evaluateAgenticButton")
            : t("NewEvaluation.evaluateStandardButton")}
        </Button>
      </Stack>

      {/* Upgrade prompt when tier limit is reached */}
      <UpgradePrompt
        open={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        currentUsage={tierLimitInfo.currentUsage}
        limit={tierLimitInfo.limit}
      />
    </Box>
  );
}
